import { z } from 'zod';

import {
  REMINDER_ENTITY_TYPES,
  REMINDER_PRIORITIES,
  REMINDER_STATUSES,
  type ReminderStatus,
} from './reminder-constants.js';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null));

export const createReminderInput = z.object({
  title: z.string().trim().min(1).max(200),
  description: optionalText(4_000),
  entityType: z.enum(REMINDER_ENTITY_TYPES),
  entityId: z.string().uuid(),
  dueAt: z.string().datetime({ offset: true }),
  priority: z.enum(REMINDER_PRIORITIES).optional().default('medium'),
  assigneeUserIds: z.array(z.string().uuid()).max(50).optional().default([]),
});
export type CreateReminderInput = z.infer<typeof createReminderInput>;

export const updateReminderInput = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: optionalText(4_000).optional(),
    dueAt: z.string().datetime({ offset: true }).optional(),
    priority: z.enum(REMINDER_PRIORITIES).optional(),
    assigneeUserIds: z.array(z.string().uuid()).max(50).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateReminderInput = z.infer<typeof updateReminderInput>;

export const completeReminderInput = z.object({
  done: z.boolean().optional().default(true),
});
export type CompleteReminderInput = z.infer<typeof completeReminderInput>;

export const snoozeReminderInput = z.object({
  dueAt: z.string().datetime({ offset: true }),
});
export type SnoozeReminderInput = z.infer<typeof snoozeReminderInput>;

export const createReminderNoteInput = z.object({
  body: z.string().trim().min(1).max(8_000),
  /** Optional voice memo upload (`reminder_voice_memo`). Transcript lives in `body`. */
  uploadId: z.string().uuid().optional(),
});
export type CreateReminderNoteInput = z.infer<typeof createReminderNoteInput>;

const csvList = (allowed: readonly string[]) =>
  z
    .string()
    .trim()
    .min(1)
    .transform((v) => v.split(','))
    .pipe(z.array(z.enum(allowed as readonly [string, ...string[]])).min(1));

export const reminderListQuery = z.object({
  status: csvList(REMINDER_STATUSES).optional(),
  mine: z.coerce.boolean().optional(),
  entityType: z.enum(REMINDER_ENTITY_TYPES).optional(),
  entityId: z.string().uuid().optional(),
  dueBefore: z.string().datetime({ offset: true }).optional(),
  dueAfter: z.string().datetime({ offset: true }).optional(),
  q: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
});
export type ReminderListQuery = z.infer<typeof reminderListQuery>;

export interface ReminderSummary {
  counts: Record<ReminderStatus, number>;
  mineDueToday: number;
  mineOverdue: number;
  topOverdue: number;
}
